import React from 'react';
import { Link } from 'react-router-dom';
import { Terminal, Key, Code2, ArrowRight, Zap, Lock, Cpu, Network } from 'lucide-react';

export default function Docs() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
      <span className="text-[10px] uppercase tracking-widest text-[#FF6B1A]">// docs</span>
      <h1 className="display text-3xl md:text-5xl font-semibold mt-2 leading-tight">
        Build with RevCore. <span className="gradient-text">In any stack.</span>
      </h1>
      <p className="text-white/65 mt-3 max-w-2xl">
        RevCore exposes a clean HTTP API for every agent in the marketplace and every agent you build. Streaming, multi-model routing, plan-aware quotas — all included.
      </p>

      <div className="mt-8 grid lg:grid-cols-3 gap-4">
        {[
          { icon: Zap, t: 'Streaming-first', d: 'SSE token-by-token responses for chat-grade UX in any UI.' },
          { icon: Cpu, t: 'Multi-model', d: 'Pin a model, or let RevCore auto-route around outages.' },
          { icon: Network, t: 'Stateful sessions', d: 'Conversations persist; rehydrate context across runs.' },
          { icon: Lock, t: 'Auth-aware', d: 'Cookie or Bearer; plan-aware quotas enforced server-side.' },
          { icon: Code2, t: 'Idempotent', d: 'Safe retries; webhook handlers tolerate replays.' },
          { icon: Key, t: 'API keys (Pro+)', d: 'Programmatic access for Pro and Enterprise plans — shipping next.' },
        ].map((f) => (
          <div key={f.t} className="card-base card-hover p-5">
            <f.icon className="h-5 w-5 text-[#FF6B1A] mb-3" />
            <div className="display text-base font-semibold">{f.t}</div>
            <p className="text-sm text-white/65 mt-1">{f.d}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 card-base p-6">
        <div className="flex items-center gap-2 mb-3">
          <Terminal className="h-4 w-4 text-[#39FF14]" />
          <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// quickstart — chat with any agent (streaming)</span>
        </div>
        <pre className="mono text-xs leading-6 overflow-x-auto bg-black/40 rounded-xl p-4 border border-border/60 text-white/85">
{`curl -N -X POST https://revcoreai.com/api/chat/stream \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <YOUR_SESSION_TOKEN>" \\
  -d '{
    "agent_id": "agent_xxxxxxxxxxxx",
    "message": "Draft a 3-step outbound sequence for a VP Sales at a 200-employee fintech."
  }'

# Server-Sent Events:
# data: {"type":"start","conversation_id":"conv_..."}
# data: {"type":"chunk","content":"Step "}
# data: {"type":"chunk","content":"1: ..."}
# data: {"type":"done","conversation_id":"conv_..."}`}
        </pre>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="card-base p-5">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF6B1A]">GET /api/agents</div>
          <div className="text-sm text-white/80 mt-1">List the marketplace catalog — filter by category or search.</div>
        </div>
        <div className="card-base p-5">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF6B1A]">POST /api/builder/agents</div>
          <div className="text-sm text-white/80 mt-1">Create a custom agent (requires Pro+).</div>
        </div>
        <div className="card-base p-5">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF6B1A]">GET /api/conversations</div>
          <div className="text-sm text-white/80 mt-1">List your conversations; rehydrate any one.</div>
        </div>
        <div className="card-base p-5">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF6B1A]">GET /api/usage/me</div>
          <div className="text-sm text-white/80 mt-1">Inspect your current-period quota.</div>
        </div>
      </div>

      <div className="mt-10 card-base p-6 relative overflow-hidden">
        <div className="absolute -right-12 -top-16 h-56 w-56 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-16 h-44 w-44 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// access</span>
            <h3 className="display text-xl font-semibold mt-1">Full API reference + API keys shipping with Pro.</h3>
            <p className="text-sm text-white/65 mt-1 max-w-md">Sign in to get a session token for testing. Programmatic API keys for production deployments land next sprint.</p>
            <a href="mailto:info@revcoreai.com?subject=RevCore%20API%20access" className="mt-3 inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#39FF14] transition-colors">
              Questions? <span className="mono text-[#39FF14]">info@revcoreai.com</span>
            </a>
          </div>
          <div className="flex gap-2">
            <Link to="/login" className="btn-primary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-medium">
              Get token <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/pricing" className="btn-secondary inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm">
              See Pro
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
